import { isEmpty } from 'lodash'
import { QF_Events, QF_EventsArgs } from 'model/QF_Events'
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  WheelEventHandler,
} from 'react'
import { paddingTopFix } from '../Highlights/HighlightConsts'
import { Vect2D, ZoomAndPanImgProps } from '../ZoomAndPanImg'
import { useZoomAndPanRefs } from './useZoomAndPanRefs'
import { useZoomAndPanStates } from './useZoomAndPanStates'

export const useZoomAndPanEffects = (
  props: ZoomAndPanImgProps,
  refs: ReturnType<typeof useZoomAndPanRefs>,
  states: ReturnType<typeof useZoomAndPanStates>,
  onScaleChanged?: (scale: number) => void,
) => {
  const {
    divRef,
    imgRef,
    divWrapperRef,
    panning,
    startPosition,
    _options,
    firstRender,
    middleClickDoubleClick,
    rightClickDoubleClick,
  } = refs

  const {
    scale,
    setScale,
    position,
    setPosition,
    rotation,
    highlights,
    setHighlights,
    isLoading,
    setIsLoading,
  } = states

  const handleSetPosition = useCallback(
    (newPosition: Vect2D, scale: number, animate?: boolean) => {
      if (divWrapperRef.current) {
        divWrapperRef.current.style.transform = `translate(${newPosition.x}px, ${newPosition.y}px) scale(${scale})`
        divWrapperRef.current.dataset.scale = scale.toString()
        if (animate) {
          divWrapperRef.current.style.transition =
            'transform 0.4s cubic-bezier(0.645, 0.045, 0.355, 1)'
          setTimeout(() => {
            divWrapperRef.current.style.transition = 'none'
          }, 400)
        }
      }
    },
    [divWrapperRef],
  )

  useEffect(() => {
    if (onScaleChanged) {
      onScaleChanged(scale)
    }
  }, [onScaleChanged, scale])

  useEffect(() => {
    if (imgRef.current) {
      // eslint-disable-next-line react-compiler/react-compiler
      imgRef.current.style.transform = `rotate(${rotation})`
    }
  }, [rotation, imgRef])

  const fitScreen = useCallback(() => {
    if (!divRef.current || !imgRef.current) return

    if (isLoading < 1) {
      return
    }

    const containerRect = divRef.current.getBoundingClientRect()
    const imgRect = imgRef.current.getBoundingClientRect()

    const containerWidth = containerRect.width
    const containerHeight = containerRect.height

    let currentScale = Number(divWrapperRef.current?.dataset.scale)

    if (isNaN(currentScale)) {
      currentScale = 1
    }

    const imgWidth = imgRect.width / (currentScale || 1)
    const imgHeight = imgRect.height / (currentScale || 1)

    const scaleX = containerWidth / imgWidth
    const scaleY = containerHeight / imgHeight

    const minScale = Number(Math.min(scaleX, scaleY).toFixed(2))

    _options.current.minScale = minScale * 0.5

    const scaleToSet = minScale * 0.95
    const positionToSet = {
      x: Math.max(0, (containerWidth + 0) / 2 - (imgWidth * scaleToSet) / 2),
      y: Math.max(
        0,
        (containerHeight + paddingTopFix) / 2 - (imgHeight * scaleToSet) / 2,
      ),
    }

    setScale(scaleToSet)
    setPosition(positionToSet)

    handleSetPosition(positionToSet, scaleToSet)
  }, [
    handleSetPosition,
    isLoading,
    divRef,
    imgRef,
    divWrapperRef,
    _options,
    setScale,
    setPosition,
  ])

  const zoomAndPan = useCallback(
    (
      clientX: number,
      clientY: number,
      delta: number,
      absoluteScale?: number,
      absolutePosition?: Vect2D,
    ) => {
      if (!divRef.current) return

      let newScale = 1

      if (absoluteScale != null) {
        newScale = absoluteScale
      } else {
        newScale = scale - delta * _options.current.zoomSpeed
      }

      newScale = Math.max(
        _options.current.minScale,
        Math.min(_options.current.maxScale, newScale),
      )

      const scaleRatio = newScale / scale

      const getPositionRelativeToMouse = () => {
        const containerRect = divRef.current.getBoundingClientRect()
        const mouseX = clientX - containerRect.left
        const mouseY = clientY - containerRect.top
        const distX = mouseX - position.x
        const distY = mouseY - position.y
        let newPosition: Vect2D

        if (!isNaN(distX) && !isNaN(distY)) {
          newPosition = {
            x: mouseX - distX * scaleRatio,
            y: mouseY - distY * scaleRatio,
          }
        }

        return newPosition
      }

      const getPositionRelativeToAbsolute = () => {
        const containerRect = divRef.current.getBoundingClientRect()
        const containerCenterX = (containerRect.width - 70) / 2
        const containerCenterY = (containerRect.height - paddingTopFix) / 2

        const newPosition = {
          x: containerCenterX - absolutePosition.x * newScale,
          y: containerCenterY - absolutePosition.y * newScale,
        }

        return newPosition
      }

      const newPosition = absolutePosition
        ? getPositionRelativeToAbsolute()
        : getPositionRelativeToMouse()

      setPosition(newPosition)
      setScale(newScale)

      handleSetPosition(newPosition, newScale, Boolean(absoluteScale))
    },
    [
      handleSetPosition,
      position,
      scale,
      _options,
      divRef,
      setPosition,
      setScale,
    ],
  )

  const onWheel = useCallback<WheelEventHandler>(
    (e) => {
      if (!divRef.current) return

      if (_options.current.disableWheelZoom) {
        return
      }

      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        // moving vertically
        zoomAndPan(e.clientX, e.clientY, e.deltaY)
      }
    },
    [zoomAndPan, divRef, _options],
  )

  const onMouseMove = useCallback<MouseEventHandler>(
    (e) => {
      // prints the image coordinates of the mouse
      const debugDiv = document.getElementById('img-coords')

      if (debugDiv) {
        const img = imgRef.current
        if (img) {
          const imgRect = img.getBoundingClientRect()
          const x = (e.clientX - imgRect.left) / scale
          const y = (e.clientY - imgRect.top) / scale

          debugDiv.innerHTML = `x: ${x} | y: ${y}`
        }
      }

      if (!panning.current) return

      e.preventDefault()

      const img = divRef.current

      if (!img) return

      const newPosition = {
        x: e.clientX - startPosition.current.x,
        y: e.clientY - startPosition.current.y,
      }

      setPosition(newPosition)

      handleSetPosition(newPosition, scale)
    },
    [
      handleSetPosition,
      scale,
      panning,
      startPosition,
      divRef,
      imgRef,
      setPosition,
    ],
  )

  const onMouseUp = useCallback<MouseEventHandler>(() => {
    panning.current = false

    if (imgRef.current) {
      imgRef.current.style.cursor = 'grab'
    }
  }, [panning, imgRef])

  const onDoubleClick = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault()

      const img = imgRef.current
      if (!img) return

      if (e.altKey) {
        setScale(1)
        setPosition({ x: 0, y: 0 })
        handleSetPosition({ x: 0, y: 0 }, 1)
      } else if (e.ctrlKey) {
        //zoom out
        zoomAndPan(e.clientX, e.clientY, 500)
      } else {
        //zoom in
        zoomAndPan(e.clientX, e.clientY, -500)
      }
    },
    [handleSetPosition, zoomAndPan, imgRef, setScale, setPosition],
  )

  const handleSetIsLoading = useCallback(
    (src: string, isLoading: -1 | 0 | 1) => {
      setIsLoading(isLoading)
      document.dispatchEvent(
        new CustomEvent<QF_EventsArgs['PDFViewerLoaded']>(
          QF_Events.PDFViewerLoaded,
          {
            detail: {
              isLoading: isLoading,
              imgSrc: src,
            },
          },
        ),
      )
    },
    [setIsLoading],
  )

  useEffect(() => {
    handleSetIsLoading(props.src, -1)
    firstRender.current = true

    if (imgRef.current) {
      imgRef.current.style.opacity = '0'
    }
  }, [handleSetIsLoading, props.src, firstRender, imgRef])

  useEffect(() => {
    if (isLoading === 1 && imgRef.current) {
      imgRef.current.style.opacity = '1'
      fitScreen()
    }
  }, [fitScreen, isLoading, imgRef])

  useEffect(() => {
    if (!props.src || !imgRef.current) {
      return () => null
    }

    if (
      !isEmpty(highlights) &&
      !props.src.includes(
        encodeURIComponent(
          highlights[Object.keys(highlights)[0]].pdfPage.image,
        ),
      )
    ) {
      console.warn('reset highlights due to image change')
      setHighlights(undefined)
    }

    const abortController = new AbortController()

    imgRef.current.style.opacity = '0'

    if (imgRef.current?.complete && imgRef.current.naturalHeight) {
      imgRef.current.style.opacity = '1'
      handleSetIsLoading(props.src, 1)
    } else {
      handleSetIsLoading(props.src, 0)
      imgRef.current.addEventListener(
        'load',
        () => {
          handleSetIsLoading(props.src, 1)
        },
        {
          signal: abortController.signal,
        },
      )
    }

    return () => {
      abortController.abort()
    }
  }, [handleSetIsLoading, highlights, props.src, imgRef, setHighlights])

  useEffect(() => {
    if (imgRef.current && isLoading > -1) {
      if (firstRender.current) {
        firstRender.current = false
      }
    }
  }, [isLoading, imgRef, firstRender])

  const getContainerCenter = useCallback(() => {
    if (!divRef.current) return null

    const containerRect = divRef.current.getBoundingClientRect()
    const centerX = containerRect.left * 2
    const centerY = containerRect.top * 2

    return { cx: centerX, cy: centerY }
  }, [divRef])

  const onMouseDown = useCallback<MouseEventHandler>(
    (e) => {
      // e.preventDefault()

      const trackGrabStarted = () => {
        panning.current = true
        startPosition.current = {
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        }

        if (imgRef.current) {
          imgRef.current.style.cursor = 'grabbing'
        }
      }

      switch (e.button) {
        case 0: // left button
          if (props.onClick) {
            props.onClick(e as never)
          } else {
            trackGrabStarted()
          }
          break
        case 1: // middle button
          if (middleClickDoubleClick.current >= 1) {
            fitScreen()
          } else {
            trackGrabStarted()

            middleClickDoubleClick.current++

            setTimeout(() => {
              middleClickDoubleClick.current = 0
            }, 250)
          }
          break
        case 2: // right button
          if (rightClickDoubleClick.current >= 1) {
            zoomAndPan(e.clientX, e.clientY, 500)
          } else {
            rightClickDoubleClick.current++
            setTimeout(() => {
              rightClickDoubleClick.current = 0
            }, 250)
          }
          return
      }
    },
    [
      panning,
      startPosition,
      position.x,
      position.y,
      imgRef,
      props,
      middleClickDoubleClick,
      rightClickDoubleClick,
      fitScreen,
      zoomAndPan,
    ],
  )

  return {
    handleSetPosition,
    fitScreen,
    zoomAndPan,
    onWheel,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onDoubleClick,
    handleSetIsLoading,
    getContainerCenter,
  }
}
